import { useTranslation } from "react-i18next";
import Collapse from "../../common/Collapse";
import EditableNewsItem from "./EditableNewsItem";
import Button from "../../common/Button";
import { useCallback, useState } from "react";
import NewsItemModal from "./NewsItemModal";
import Loading from "../../common/Loading";
import { renderTemplateSlideItemPreview } from "../templates";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import APIManager from "../../../managers/APIManager";
import { useSelector } from "react-redux";

export default function ContentCollapse({
  newsList,
  newsLoading,
  orderBy,
  setOrderBy,
  orderByDirection,
  setOrderByDirection,
  setNewsList,
  selectedNews,
  setSelectedNews,
  styleSettings,
  selectedVideoTemplate,
  templateRestrictions,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [editingNews, setEditingNews] = useState(null);
  const [isNewsItemModalOpen, setIsNewsItemModalOpen] = useState(false);

  const siteVirgulId = useSelector(
    (state) => state.site?.selectedSite?.virgulId
  );

  const [refreshLoading, setRefreshLoading] = useState(false);

  const refreshNewsList = useCallback(async () => {
    setRefreshLoading(true);
    try {
      const response = await APIManager.fetchSiteNews({
        siteVirgulId,
        orderBy,
        orderByDirection,
      });
      setNewsList(response?.data || []);
    } catch (error) {
      console.error("Error refreshing news list:", error);
    } finally {
      setRefreshLoading(false);
    }
  }, [siteVirgulId, orderBy, orderByDirection]);

  const handleSaveNewsItem = (newsItem) => {
    if (newsItem.id?.startsWith("new-card-")) {
      // New item - add to list
      setNewsList((prev) => [newsItem, ...prev]);
      setSelectedNews((prev) => [...prev, newsItem.id]);
    } else {
      // Existing item - update in list
      setNewsList((prev) =>
        prev.map((news) => (news.id === newsItem.id ? newsItem : news))
      );
    }
    setIsNewsItemModalOpen(false);
    setEditingNews(null);
  };

  const handleCloseModal = () => {
    setIsNewsItemModalOpen(false);
    setEditingNews(null);
  };

  const handleToggleSelect = (newsId) => {
    setSelectedNews((prev) => {
      if (prev.includes(newsId)) {
        return prev.filter((id) => id !== newsId);
      }
      return [...prev, newsId];
    });
  };

  const handleEdit = (newsId, field, value) => {
    setNewsList((prev) =>
      prev.map((news) =>
        news.id === newsId ? { ...news, [field]: value } : news
      )
    );
  };

  const handleRemoveFromNewsList = (newsId) => {
    setNewsList((prev) => prev.filter((news) => news.id !== newsId));
    setSelectedNews((prev) => prev.filter((id) => id !== newsId));
  };

  const handleAddEmptyNewsItem = () => {
    const newNewsItem = {
      id: `new-card-${Date.now()}`,
      title: "",
      image: "",
      voiceText: "",
      category: "",
      subTitle: "",
    };
    setEditingNews(newNewsItem);
    setIsNewsItemModalOpen(true);
  };

  return (
    <>
      <Collapse
        title={
          <div className="flex items-center justify-between gap-2 w-full mr-4">
            <div className="font-semibold text-gray-800 text-2xl">
              {t("content")}
            </div>
            <Button
              variant="secondary"
              icon={faRefresh}
              loading={refreshLoading}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                refreshNewsList();
              }}
            >
              {t("refresh")}
            </Button>
          </div>
        }
        className="mt-6"
        bodyClassName={newsLoading ? "relative h-64" : ""}
        disabled={disabled}
        defaultOpen={!disabled}
      >
        {newsLoading ? (
          <div className="absolute inset-0 flex justify-center items-center h-64">
            <Loading />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="text-sm text-gray-500">
                  {t("items length")}: {selectedNews.length} /{" "}
                  {templateRestrictions
                    ? templateRestrictions["items[].length"] || "∞"
                    : "∞"}
                </div>
              </div>
              {newsList.length > 0 && (
                <select
                  className="bg-gray-200 focus:bg-white form-select"
                  id="orderBy"
                  name="orderBy"
                  onChange={(e) => {
                    e.stopPropagation();
                    const value = e.target.value;
                    setOrderBy(value.split("_")[0]);
                    setOrderByDirection(value.split("_")[1]);
                  }}
                  value={`${orderBy}_${orderByDirection}`}
                >
                  <option value="text_asc">{t("text (asc)")}</option>
                  <option value="text_desc">{t("text (desc)")}</option>
                  <option value="date_asc">{t("date (asc)")}</option>
                  <option value="date_desc">{t("date (desc)")}</option>
                </select>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex mb-3">
                <Button
                  className="w-full transition-all duration-300 hover:bg-empower-500 hover:text-white border-dashed border-4 border-empower-500 text-empower-500"
                  onClick={handleAddEmptyNewsItem}
                  type="button"
                >
                  {t("add new")}
                </Button>
              </div>
              {newsList.map((news) => (
                <EditableNewsItem
                  preview={(news) =>
                    renderTemplateSlideItemPreview({
                      template: selectedVideoTemplate?.name,
                      news: news,
                      styleSettings: styleSettings,
                    })
                  }
                  key={`news-item-${news.id}`}
                  news={news}
                  videoRestrictions={templateRestrictions}
                  isSelected={selectedNews.includes(news.id)}
                  isReadonly={false}
                  onToggleSelect={handleToggleSelect}
                  onEdit={handleEdit}
                  onRemove={() => handleRemoveFromNewsList(news.id)}
                  selectedVideoTemplate={selectedVideoTemplate}
                  styleSettings={styleSettings}
                />
              ))}
            </div>
          </>
        )}
      </Collapse>
      {editingNews && (
        <NewsItemModal
          selectedVideoTemplate={selectedVideoTemplate}
          styleSettings={styleSettings}
          isVisible={isNewsItemModalOpen}
          onClose={handleCloseModal}
          news={editingNews}
          videoRestrictions={templateRestrictions}
          isReadonly={false}
          onSave={handleSaveNewsItem}
        />
      )}
    </>
  );
}
